import React from "react";
import { SolanaTestTokenNotice } from "./SolanaTestTokenNotice";
import { styles } from "../../styles";
import { WalletAmountInput } from "../../base-app/components/WalletAmountInput";
import { solanaAmount } from "../helpers/amounts";
import { SolanaDepositFeeSummary } from "./SolanaFeeSummary";
import type { useSolanaDepositQuote } from "../hooks/useSolanaDepositQuote";
import type { SolanaTokenInfo } from "../../config/solana";

interface SolanaShieldTabProps {
  paymentRecipient?: string;
  paymentError?: string | null;
  tokenLocked?: boolean;
  tokens: SolanaTokenInfo[];
  selectedToken: SolanaTokenInfo;
  setSelectedToken: (token: SolanaTokenInfo) => void;
  amount: string;
  memo: string;
  setMemo: (value: string) => void;
  walletBalance: string | null;
  depositQuote: ReturnType<typeof useSolanaDepositQuote>;
  connected: boolean;
  busy: boolean;
  onShield: () => void | Promise<void>;
}

export function SolanaShieldTab({
  paymentRecipient,
  paymentError,
  tokenLocked,
  tokens,
  selectedToken,
  setSelectedToken,
  amount,
  memo,
  setMemo,
  walletBalance,
  depositQuote,
  connected,
  busy,
  onShield,
}: SolanaShieldTabProps) {
  const value = solanaAmount(amount, selectedToken);
  const quote = depositQuote.quote;
  const error =
    paymentError ||
    (amount.trim() && !value
      ? "Enter a valid amount."
      : value && quote && value > quote.max
        ? "Insufficient wallet balance after network fees and rent. Select Max."
        : null);
  const disabled =
    !connected ||
    busy ||
    depositQuote.loading ||
    !!depositQuote.error ||
    !quote ||
    !value ||
    !!error;
  return (
    <div>
      <p style={{ fontSize: "14px", color: "#b0c4de", marginBottom: "16px" }}>
        {paymentRecipient
          ? "Pay from your connected wallet into the recipient's private balance. You do not need a private balance. Your wallet and deposit amount are public on-chain."
          : "Deposit tokens into your private wallet. You will be prompted to sign the transaction."}
      </p>
      {paymentRecipient && (
        <p
          className="wallet-review-context"
          style={{ overflowWrap: "anywhere" }}
        >
          Recipient private address: {paymentRecipient}
        </p>
      )}
      <label htmlFor="solana-deposit-token" style={styles.label}>
        Token
      </label>
      <select
        id="solana-deposit-token"
        disabled={tokenLocked}
        value={selectedToken.symbol}
        onChange={(event) => {
          const token = tokens.find(
            (candidate) => candidate.symbol === event.target.value,
          );
          if (token) setSelectedToken(token);
        }}
        style={{ ...styles.input, color: "#e8eeff" } as React.CSSProperties}
      >
        {tokens.map((token) => (
          <option
            key={token.symbol}
            value={token.symbol}
            style={{ background: "#1a1a2e" }}
          >
            {token.symbol} — {token.name}
          </option>
        ))}
      </select>
      <SolanaTestTokenNotice token={selectedToken} />
      <label htmlFor="solana-deposit-amount" style={styles.label}>
        Amount
        {walletBalance !== null
          ? ` (wallet: ${walletBalance} ${selectedToken.symbol})`
          : ""}
      </label>
      <WalletAmountInput
        id="solana-deposit-amount"
        value={amount}
        onChange={(event) => depositQuote.changeAmount(event.target.value)}
        placeholder="0.0"
        onMax={() => void depositQuote.selectMax()}
        maxDisabled={!connected || busy}
        disabled={!connected || busy}
        aria-invalid={!!error}
        aria-describedby={error ? "solana-deposit-error" : undefined}
      />
      {error && (
        <p
          id="solana-deposit-error"
          role="status"
          style={{ color: "#ff5263", fontSize: 13 }}
        >
          {error}
        </p>
      )}
      <SolanaDepositFeeSummary
        token={selectedToken}
        amount={value ?? 0n}
        quote={quote}
        error={depositQuote.error}
      />
      <label htmlFor="solana-deposit-memo" style={styles.label}>
        Memo (private, optional)
      </label>
      <input
        id="solana-deposit-memo"
        style={styles.input}
        type="text"
        value={memo}
        onChange={(event) => setMemo(event.target.value)}
        placeholder={
          paymentRecipient
            ? "Private memo for the recipient"
            : "Only you can read this"
        }
      />
      <button
        className="wallet-primary"
        disabled={disabled}
        onClick={() => {
          depositQuote.acceptAmount();
          void onShield();
        }}
      >
        {busy ? "Working…" : "Continue"}
      </button>
    </div>
  );
}
